/**
 * Float32 → 24-bit LE packing for the RT session recorder.
 *
 * No dither (kept simple per spec). Positive full-scale maps to 0x7FFFFF and
 * negative full-scale to 0x800000, matching the int24 range exactly (same
 * convention as `float32ToInt24` in recorder/recorder.ts).
 */

export const BYTES_PER_SAMPLE_24 = 3

/**
 * Pack Float32 samples in [-1, 1] into 24-bit signed little-endian PCM,
 * writing into `out` starting at `outOffset`. Out-of-range input is clamped.
 *
 * @returns number of bytes written (always `input.length * 3`).
 */
export function packFloat32To24LE(input: Float32Array, out: Uint8Array, outOffset: number): number {
  if (outOffset + input.length * 3 > out.length) {
    throw new Error('packFloat32To24LE: output buffer too small')
  }
  let o = outOffset
  for (let i = 0; i < input.length; i++) {
    const raw = input[i] ?? 0
    const s = raw < -1 ? -1 : raw > 1 ? 1 : raw
    const v = Math.round(s < 0 ? s * 0x800000 : s * 0x7fffff)
    out[o] = v & 0xff
    out[o + 1] = (v >> 8) & 0xff
    out[o + 2] = (v >> 16) & 0xff
    o += 3
  }
  return o - outOffset
}

/**
 * Aggregates packed 24-bit PCM into fixed-size blocks for the sink
 * (~144 KB ≈ 1 s @ 48 kHz mono 24-bit), emitting `onBlock` whenever a block
 * fills. Incoming Float32 chunks (128-frame worklet quanta) are packed into an
 * internal carry buffer; a sample whose 3 bytes straddle a block boundary is
 * split byte-exactly across blocks, so the emitted byte stream is continuous
 * regardless of block size vs. chunk size alignment.
 */
export class Pcm24BlockWriter {
  private readonly block: Uint8Array
  private filled = 0
  /** Scratch for packing one incoming chunk before the carry-copy. */
  private scratch = new Uint8Array(0)

  constructor(
    readonly blockBytes: number,
    private readonly onBlock: (block: Uint8Array) => void,
  ) {
    if (blockBytes <= 0) throw new Error('Pcm24BlockWriter: blockBytes must be > 0')
    this.block = new Uint8Array(blockBytes)
  }

  /** Bytes currently buffered (not yet emitted). */
  get pendingBytes(): number {
    return this.filled
  }

  /** Pack `samples` and append to the block, emitting full blocks as needed. */
  push(samples: Float32Array): void {
    const need = samples.length * BYTES_PER_SAMPLE_24
    if (this.scratch.length < need) this.scratch = new Uint8Array(need)
    packFloat32To24LE(samples, this.scratch, 0)

    let srcOff = 0
    while (srcOff < need) {
      const free = this.blockBytes - this.filled
      const n = Math.min(free, need - srcOff)
      this.block.set(this.scratch.subarray(srcOff, srcOff + n), this.filled)
      this.filled += n
      srcOff += n
      if (this.filled === this.blockBytes) {
        // Emit a copy — the receiver may hold it across async writes.
        this.onBlock(this.block.slice())
        this.filled = 0
      }
    }
  }

  /**
   * Emit whatever is buffered as a partial block (used on the 3 s durability
   * tick and on stop). Returns the number of bytes flushed.
   */
  flush(): number {
    if (this.filled === 0) return 0
    const n = this.filled
    this.onBlock(this.block.slice(0, n))
    this.filled = 0
    return n
  }
}
